import isVizio from '@accedo/xdk-device-vizio-smartcast-contrib/esm/detection'
import { Img, Lightning, Registry, VideoPlayer } from '@lightningjs/sdk'
import Hls from 'hls.js'

import { Images } from '../../constants/paths'
import { EntityType } from '../../types/flo-enums'
import { CardItemModel, PlayerModel } from '../../types/flo-experience'
import { Flo } from '../../types/flo-live-api'
import { getAsset } from '../../utils/image'
import { isStagingEnabled } from '../../utils/settingsUtils'
import {
  getYouboraVideoOptions,
  registerYouboraPlugin,
  youboraFireStart,
} from '../../utils/youbora'
import { getUnifiedCdnPlayback } from '../player/utils'
import TvBrowseLayout from '../TvBrowseLayout/TvBrowseLayout'
import { HIDDEN } from '../TvCards/templates'
import SpotlightMetadata, { SpotlightMetadataData } from './SpotlightMetadata'

const config = {
  debug: isStagingEnabled(),
  startLevel: 0, // start with the lowest quality (smallest segments)
  lowLatencyMode: true, // enable LL-HLS if available
  autoStartLoad: true, // start loading segments immediately
  maxBufferLength: 5, // do not buffer too much ahead
  maxBufferHole: 0.5, // helps avoid playback stalls
  backBufferLength: 0, // no need to keep past data on previews
}

const SEVENTY_FIVE_SECONDS_IN_MS = 75000
const VIZIO_HEIGHT_ADJUSTMENT = 35

export default class Spotlight extends Lightning.Component {
  private _player: Hls
  private _authHeader: string | undefined
  private _streamTimeout = -1
  private _streamId: null | number
  private _data: CardItemModel
  private _entityType: EntityType
  private _isFocusable = false
  private _hasBgImage = false
  private readonly _playerLoaderBind = this.playerLoader.bind(this)
  private readonly _playerUnloaderBind = this.playerUnloader.bind(this)

  static _template() {
    return {
      collision: true,

      Container: {
        w: 1920,
        h: 1080,
        Overlay: {
          x: -1,
          texture: Lightning.Tools.getSvgTexture(getAsset(Images.SPOTLIGHT_BG), 1745, 949),
          zIndex: -1,
        },
        Logo: {
          x: 1221,
          y: 210,
          texture: Lightning.Tools.getSvgTexture(getAsset(Images.LOGO_FADED), 259, 259),
          zIndex: -2,
          alpha: HIDDEN,
        },
        BackgroundImageOverlay: {
          x: 562,
          texture: Lightning.Tools.getSvgTexture(getAsset(Images.BACKGROUND_IMG_MASK), 1182, 643),
          zIndex: -1,
          alpha: HIDDEN,
        },
        BackgroundImage: {
          x: 988,
          y: -91,
          w: 985,
          h: 703,
          texture: null,
          zIndex: -2,
          alpha: HIDDEN,
        },
        VerticalLogo: {
          visible: false,
          w: 334,
          h: 28,
          x: 1314,
          y: 54,
        },
        Metadata: {
          type: SpotlightMetadata,
        },
      },
    }
  }

  get overlay() {
    return this.tag('Overlay')
  }

  get metadata() {
    return this.tag('Metadata') as SpotlightMetadata
  }

  get defaultLogo() {
    return this.tag('Logo')
  }

  get verticalLogo() {
    return this.tag('VerticalLogo')
  }

  set offset(v) {
    this.metadata.h = v
  }

  get backgroundImage() {
    return this.tag('BackgroundImage')
  }

  get bgImgOverlay() {
    return this.tag('BackgroundImageOverlay')
  }

  set entityType(v: EntityType) {
    this._entityType = v
  }

  get isFocusable() {
    return this._isFocusable
  }

  _init() {
    this.bgImgOverlay.on('txLoaded', () => {
      this.backgroundImage.on('txLoaded', () => {
        this.backgroundImage.setSmooth('alpha', 1)
      })
    })

    this.overlay.on('txLoaded', () => {
      this.defaultLogo.setSmooth('alpha', 1)
    })
  }

  _firstActive() {
    VideoPlayer.consumer(this)
    VideoPlayer.loader(this._playerLoaderBind)
    VideoPlayer.unloader(this._playerUnloaderBind)
  }

  _active() {
    let x = 0
    let y = 176
    let width = 1744
    let height = 948

    if (isVizio && this.stage.w === 1280) {
      // Vizio forces the aspect ratio. If the dimensons don't comply, a black letterbox effect is applied to keep the ratio.
      // Hence, we need to adjust the video element's dimension to make this black border disappear. This also implies stretching
      // the overlay asset, otherwise the video would overflow beyond it
      height += VIZIO_HEIGHT_ADJUSTMENT
      this.overlay.h = height + 1
    } else if (this.stage.w === 1280) {
      // Increases dimensions of video player by 3/2 so that when it gets remapped to 720p it remains as if it were 1080p
      x *= 1.5
      y *= 1.5
      width *= 1.5
      height *= 1.5
    }

    VideoPlayer.position(x, y)
    VideoPlayer.size(width, height)
  }

  _inactive() {
    if (this._streamTimeout) {
      Registry.clearTimeout(this._streamTimeout)
    }

    VideoPlayer.close()
  }

  setupHlsPlayer(videoEl: HTMLVideoElement) {
    videoEl.muted = true
    videoEl.style.objectFit = 'fill'
    this._player = new Hls({
      ...config,
      xhrSetup: (xhr) => {
        if (this._authHeader) {
          xhr.setRequestHeader('Authorization', this._authHeader)
        }
      },
    })
  }

  async playerLoader(url: string, videoEl: HTMLVideoElement) {
    return new Promise<void>((resolve) => {
      this.setupHlsPlayer(videoEl)

      this._player.loadSource(url)

      getYouboraVideoOptions({
        title: this?._data?.title?.text,
        data: this._data.action as unknown as PlayerModel,
        isLive: true,
        resourceUrl: url,
        isUpNext: false,
      }).then((options) => {
        // The spotlight might get destroy midway through initialization
        if (!this.active) return

        registerYouboraPlugin(this._player, options, () => {
          // The spotlight might get destroy midway through initialization
          if (!this.active) return

          this._player.attachMedia(videoEl)
          this._player.autoLevelCapping = 1
          youboraFireStart()
          resolve()
        })
      })
    })
  }

  async playerUnloader() {
    return new Promise<void>((resolve) => {
      if (this._player) {
        this._player.stopLoad()
        this._player.destroy()
        this._player = null
      }

      resolve()
    })
  }

  $videoPlayerEvent(eventName) {
    if (eventName === 'LoadedMetadata') {
      const tvBrowseLayout = this.parent

      if (tvBrowseLayout instanceof TvBrowseLayout) {
        const curCardStreamId = tvBrowseLayout.curCardStreamId

        if (!curCardStreamId || curCardStreamId !== this._streamId) {
          this.clearLivePreview()
        }
      }
    }

    if (eventName === 'LoadedData') {
      this.toggleLogo(false)
    }

    if (eventName === 'Playing') {
      this.startPreviewTimeout()
    }

    if (eventName === 'Clear') {
      this.toggleLogo(!this._hasBgImage)
    }

    if (eventName === 'Ended') {
      this.clearLivePreview()
    }

    if (eventName === 'Error') {
      VideoPlayer.close()
    }
  }

  startPreview(stream: Flo.Live.PremiumStreamResponse, data: CardItemModel) {
    if (stream?.stream?.id === this._streamId) {
      return
    }

    this._streamId = stream?.stream?.id
    this._data = data

    const { url, headers } = getUnifiedCdnPlayback(stream.v2, 0)
    this._authHeader = headers?.Authorization

    VideoPlayer.close() // Reset player before loading a new src
    VideoPlayer.open(url)
  }

  startPreviewTimeout() {
    if (this._streamTimeout) {
      Registry.clearTimeout(this._streamTimeout)
    }

    this._streamTimeout = Registry.setTimeout(() => {
      this.clearLivePreview()
    }, SEVENTY_FIVE_SECONDS_IN_MS)
  }

  clearLivePreview() {
    this._streamId = null

    if (this._streamTimeout) {
      Registry.clearTimeout(this._streamTimeout)
    }

    if (VideoPlayer.visible) {
      VideoPlayer.clear()
      VideoPlayer.close()
    }
  }

  toggleLogo(toggle = false) {
    this.defaultLogo.visible = !!toggle
  }

  renderSpotlightData(data: SpotlightMetadataData) {
    if (data?.spotlight_metadata?.style === 'entity') {
      this.overlay.visible = false

      this.bgImgOverlay.alpha = 1

      // Lightning's Img utility crashes if a null is provided, so if there is no background_image, better not call it
      if (data?.spotlight_metadata?.background_image?.url) {
        this._hasBgImage = true
        this.backgroundImage.patch({
          texture: Img(data.spotlight_metadata.background_image.url)?.cover(985, 703),
        })
      }
    }

    const { spotlight_metadata } = data

    if (spotlight_metadata?.vertical_logo) {
      this.verticalLogo.patch({
        texture: Img(spotlight_metadata.vertical_logo.url).cover(334, 28),
        visible: true,
      })
    }

    if (data?.spotlight_metadata?.action_buttons) {
      this._isFocusable = true
    }

    this.metadata.data = data
  }
}
